import { useState, memo, type MouseEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { MapPin, Users, Building2, Heart, Star, Check } from 'lucide-react'
import Card from '@/components/ui/Card'
import Badge from '@/components/ui/Badge'
import type { FacilitySummary } from '@/types/facility'
import type { EvaluationGrade } from '@/types/common'
import { FACILITY_TYPE_LABELS } from '@/types/common'

interface FacilityCardProps {
  facility: FacilitySummary
  isBookmarked?: boolean
  onToggleBookmark?: (facilityId: number, isBookmarked: boolean) => void
  isCompareMode?: boolean
  isCompareSelected?: boolean
  onCompareToggle?: (facilityId: number) => void
  onCardClick?: (facilityId: number) => void
  /**
   * 카드 variant (기본은 facility.premiumPlan 기반 자동 분기)
   * - 'default': 무료 시설 스타일 (h-14 썸네일, 컴팩트)
   * - 'premium': 확장 스타일 (h-20 썸네일, 파란 테두리+그림자, 그라데이션 배경)
   */
  variant?: 'default' | 'premium'
}

const PROGRAM_TAG_STYLES: Record<string, { icon: string; label: string; className: string }> = {
  '인지활동': { icon: '🧠', label: '인지', className: 'bg-blue-50 text-blue-600' },
  '신체활동': { icon: '💪', label: '신체', className: 'bg-green-50 text-green-600' },
  '사회/여가': { icon: '🎭', label: '여가', className: 'bg-amber-50 text-amber-600' },
  '기타': { icon: '📋', label: '기타', className: 'bg-gray-50 text-gray-500' },
}

const gradeBarClass: Record<string, string> = {
  A: 'grade-bar-a',
  B: 'grade-bar-b',
  C: 'grade-bar-c',
  D: 'grade-bar-d',
  E: 'grade-bar-e',
}

/** 시설 카드 컴포넌트 (React.memo로 불필요한 리렌더링 방지) */
const FacilityCard = memo(function FacilityCard({ facility, isBookmarked, onToggleBookmark, isCompareMode, isCompareSelected, onCompareToggle, onCardClick, variant }: FacilityCardProps) {
  const navigate = useNavigate()
  // variant prop 우선, 없으면 premiumPlan 기반 자동 분기
  const isPremium = variant ? variant === 'premium' : !!facility.premiumPlan

  const detailPath = `/facilities/${facility.id}`

  /**
   * 카드 클릭이 상세 이동이 아닌 다른 동작(비교 선택 / 지도 선택)을 하는 모드인가.
   * 이때는 시설명 링크도 이동하지 않고 카드 동작을 따라야 한다.
   */
  const clickDoesNotNavigate = !!((isCompareMode && onCompareToggle) || onCardClick)

  const handleClick = () => {
    if (isCompareMode && onCompareToggle) {
      onCompareToggle(facility.id)
      return
    }
    if (onCardClick) {
      onCardClick(facility.id)
      return
    }
    navigate(detailPath)
  }

  /**
   * 시설명 링크의 클릭 처리.
   *
   * - 일반 모드: 링크가 이동을 맡으므로 카드 onClick 으로 버블링시키지 않는다(중복 navigate 방지).
   * - 비교/지도 선택 모드: 이동을 막고 버블링만 시켜 카드 동작(체크·선택)이 처리하게 둔다.
   */
  const handleNameClick = (e: MouseEvent) => {
    if (clickDoesNotNavigate) {
      e.preventDefault()
      return
    }
    e.stopPropagation()
  }

  const typeLabel =
    FACILITY_TYPE_LABELS[facility.facilityType as keyof typeof FACILITY_TYPE_LABELS] ??
    facility.facilityType

  const gradeKey = facility.evaluationGrade as string | undefined

  return (
    <Card
      clickable
      onClick={handleClick}
      noPadding
      className={`relative overflow-hidden ${isCompareSelected ? 'ring-2 ring-cb-primary' : ''} ${
        isPremium
          ? 'border-2 border-cb-primary/40 shadow-md shadow-cb-primary/10 bg-gradient-to-r from-cb-primary/5 via-white to-white'
          : ''
      }`}
      role="article"
      aria-label={`${facility.name} 시설 정보${isPremium ? ' (프리미엄)' : ''}`}
    >
      {/* 프리미엄 상단 띠 (2-tier 재편: 단일 PREMIUM) */}
      {isPremium && (
        <div className="h-1 w-full bg-gradient-to-r from-blue-400 via-indigo-300 to-blue-400" />
      )}

      <div className="flex">
        {/* 비교 모드 체크 */}
        {isCompareMode && (
          <div className={`absolute top-2 left-2 z-10 flex h-6 w-6 items-center justify-center rounded-full ${isCompareSelected ? 'bg-cb-primary' : 'bg-white border-2 border-cb-gray-300'}`}>
            {isCompareSelected && <Check size={14} className="text-white" />}
          </div>
        )}

        {/* 좌측 등급 컬러 바 (프리미엄이 아닌 경우만) */}
        {!isPremium && gradeKey && (
          <div
            className={`w-1 flex-shrink-0 ${gradeBarClass[gradeKey] ?? 'bg-cb-gray-300'}`}
            aria-hidden="true"
          />
        )}

        <div className="flex-1 min-w-0 p-[var(--spacing-card)]">
          {/* 1행: 썸네일 + 시설명 + 북마크 */}
          <div className="flex items-center gap-2.5">
            <FacilityThumbnail url={facility.thumbnailUrl} name={facility.name} isPremium={isPremium} />
            {/* 시설명은 반드시 진짜 <a> 여야 한다.
                카드 전체는 div+onClick 이라 크롤러에게는 링크가 아니고, 그 결과 지역 페이지에서
                시설 상세로 가는 크롤 경로가 0개였다(2026-08-31 라이브 실측: 의정부시 페이지의
                href="/facilities/{id}" 0건). 앵커 텍스트가 곧 "이 URL 은 이 시설명"이라는
                가장 강한 신호라, 시설명만 감싸 앵커 텍스트를 시설명 하나로 정확히 맞춘다.
                카드 전체를 <a> 로 감싸지 않는 이유 — 내부에 북마크/비교/상세보기 버튼이 있어
                <a> 안에 interactive content 가 들어가는 무효 HTML 이 되고, 앵커 텍스트도
                주소·정원까지 섞여 희석된다. */}
            <h3
              className={`truncate flex-1 min-w-0 ${
                isPremium
                  ? 'text-lg font-extrabold text-cb-gray-900 tracking-tight'
                  : 'text-body font-bold text-cb-gray-900'
              }`}
            >
              <Link to={detailPath} onClick={handleNameClick} className="hover:underline">
                {facility.name}
              </Link>
            </h3>
            {onToggleBookmark && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleBookmark(facility.id, !!isBookmarked)
                }}
                className="flex-shrink-0 p-1"
                aria-label={isBookmarked ? '관심 시설 해제' : '관심 시설 추가'}
              >
                <Heart
                  size={18}
                  className={isBookmarked ? 'fill-cb-warm text-cb-warm' : 'text-cb-gray-300 hover:text-cb-warm/60'}
                />
              </button>
            )}
          </div>

          {/* 2행: 배지 모음 (프리미엄 + 등급 + 거리) */}
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            {isPremium && (
              <Badge variant="premium" />
            )}
            {facility.evaluationGrade && (
              <Badge variant="grade" grade={facility.evaluationGrade as EvaluationGrade} />
            )}
            {facility.distanceKm != null && (
              <span className="inline-flex items-center gap-1 rounded-full bg-cb-primary-light px-2 py-0.5 text-caption font-bold text-cb-primary">
                <MapPin size={11} />
                {facility.distanceKm < 1
                  ? `${Math.round(facility.distanceKm * 1000)}m`
                  : `${facility.distanceKm.toFixed(1)}km`}
              </span>
            )}
            {facility.hasDementiaCare && (
              <span className="rounded-full bg-cb-secondary-light px-2 py-0.5 text-caption font-semibold text-cb-secondary">
                치매전담
              </span>
            )}
          </div>

          {/* 3행: 유형 배지 + 주소 */}
          <div className="mt-1.5 flex items-center gap-1.5 text-body-sm text-cb-gray-500">
            <Badge variant="type" className="flex-shrink-0 whitespace-nowrap">{typeLabel}</Badge>
            <span className="text-cb-gray-300">|</span>
            <MapPin size={13} className="flex-shrink-0 text-cb-primary" />
            <span className="truncate">{facility.address}</span>
          </div>

          {/* 4행: 정원/현원 */}
          <div className="mt-2 flex items-center gap-1.5 text-body-sm text-cb-gray-600">
            <Users size={14} className="flex-shrink-0 text-cb-gray-400" />
            {facility.capacity != null && facility.capacity > 0 ? (
              <>
                <span>정원 <strong className="text-cb-gray-800">{facility.capacity}</strong>명</span>
                <span className="text-cb-gray-300">|</span>
                <span>현원 <strong className="text-cb-gray-800">{facility.currentCount ?? 0}</strong>명</span>
              </>
            ) : (
              <span className="text-cb-gray-400">정원 정보 없음</span>
            )}
          </div>

          {/* 5행: 리뷰 별점 + 프로그램 태그 + 상세보기 */}
          <div className="mt-2 flex items-center gap-2">
            {facility.reviewCount != null && facility.reviewCount > 0 && (
              <span className="inline-flex items-center gap-1 text-caption font-semibold text-amber-600">
                <Star size={12} className="fill-amber-400 text-amber-400" />
                {facility.reviewAvgRating}
                <span className="text-cb-gray-400 font-normal">({facility.reviewCount})</span>
              </span>
            )}
            {facility.programTypes && facility.programTypes.split(',').map((type) => {
              const trimmed = type.trim()
              const style = PROGRAM_TAG_STYLES[trimmed] ?? PROGRAM_TAG_STYLES['기타']
              if (!style) return null
              return (
                <span
                  key={trimmed}
                  className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-bold ${style.className}`}
                >
                  {style.icon} {style.label}
                </span>
              )
            })}
            {/* 버튼이 아니라 링크다 — 하는 일이 페이지 이동이고, 새 탭 열기·크롤 경로가 함께 따라온다.
                카드 onClick(지도 선택 등)이 같이 실행되지 않도록 버블링만 막는다. */}
            {onCardClick && (
              <Link
                to={detailPath}
                onClick={(e) => e.stopPropagation()}
                className={`ml-auto flex-shrink-0 rounded-lg px-3 py-1.5 text-caption font-semibold transition-colors ${
                  isPremium
                    ? 'bg-cb-primary text-white hover:bg-cb-primary/90 shadow-sm'
                    : 'bg-cb-primary/10 text-cb-primary hover:bg-cb-primary/20'
                }`}
              >
                상세보기
              </Link>
            )}
          </div>
        </div>
      </div>
    </Card>
  )
})

export default FacilityCard

/**
 * 시설 썸네일
 * - default: 56x56 (h-14 w-14)
 * - premium: 80x80 (h-20 w-20) — 약 1.43배 확대, 파란 보더 강조
 */
function FacilityThumbnail({
  url,
  name,
  isPremium = false,
}: {
  url?: string | null
  name: string
  isPremium?: boolean
}) {
  const [imgError, setImgError] = useState(false)
  const sizeClass = isPremium ? 'h-20 w-20' : 'h-14 w-14'
  const ringClass = isPremium ? 'ring-2 ring-cb-primary/30' : ''
  const pixelSize = isPremium ? 80 : 56

  if (url && !imgError) {
    return (
      <div className={`${sizeClass} ${ringClass} flex-shrink-0 overflow-hidden rounded-xl bg-cb-gray-100`}>
        <img
          src={url}
          alt={`${name} 시설 사진`}
          loading="lazy"
          width={pixelSize}
          height={pixelSize}
          onError={() => setImgError(true)}
          className="h-full w-full object-cover"
        />
      </div>
    )
  }

  return (
    <div className={`${sizeClass} ${ringClass} flex-shrink-0 flex items-center justify-center rounded-xl bg-cb-gray-50`}>
      <Building2 size={isPremium ? 32 : 22} className="text-cb-gray-300" />
    </div>
  )
}
